import {
  useMemo,
  useState,
} from 'react';

import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';

import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import AccountTreeRoundedIcon
  from '@mui/icons-material/AccountTreeRounded';
import ExpandLessRoundedIcon
  from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon
  from '@mui/icons-material/ExpandMoreRounded';
import InsightsRoundedIcon
  from '@mui/icons-material/InsightsRounded';
import OpenInNewRoundedIcon
  from '@mui/icons-material/OpenInNewRounded';

import {
  getMarginCenterProductEconomicStudy,
} from '../services/marginCenterService';

import type {
  MarginCenterComparisonMode,
} from '../types/marginCenter';

import type {
  MarginCenterProductEconomicStudyFinding,
  MarginCenterProductEconomicStudyInvestigationPattern,
  MarginCenterProductEconomicStudyInvestigationPatternCode,
  MarginCenterProductEconomicStudyParams,
} from '../types/productEconomicStudy';

import {
  formatMarginCenterCurrency as formatMoney,
  formatMarginCenterNumber as formatNumber,
} from '../utils/numberFormat';


interface ProductEconomicStudyPanelProps {
  productId: string;
  startDate?: string | null;
  endDate?: string | null;
  comparisonMode: MarginCenterComparisonMode;
  comparisonStartDate?: string | null;
  comparisonEndDate?: string | null;
  currencyCode: string | null;
}


function formatPercentage(
  value: number | null | undefined,
): string {
  if (value === null || value === undefined) {
    return '—';
  }

  return `${formatNumber(value, 2)} %`;
}


function formatSignedMoney(
  value: number | null | undefined,
  currencyCode: string | null,
): string {
  if (value === null || value === undefined) {
    return '—';
  }

  const formatted = formatMoney(
    Math.abs(value),
    currencyCode,
  );

  if (value > 0) return `+${formatted}`;
  if (value < 0) return `−${formatted}`;

  return formatted;
}


function readNumber(
  value: string | number | null | undefined,
): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value)
      ? value
      : null;
  }

  if (
    typeof value === 'string'
    && value.trim() !== ''
  ) {
    const parsed = Number(value);

    return Number.isFinite(parsed)
      ? parsed
      : null;
  }

  return null;
}


function readString(
  value: string | number | null | undefined,
): string | null {
  if (
    typeof value === 'string'
    && value.trim() !== ''
  ) {
    return value;
  }

  return null;
}


function movementColor(
  value: number | null | undefined,
): string {
  if (
    value === null
    || value === undefined
    || value === 0
  ) {
    return '#e2e8f0';
  }

  return value > 0
    ? '#34d399'
    : '#fb7185';
}


function effectLabel(
  value: string | null,
): string {
  if (value === 'quantity') return 'Cantidad';
  if (value === 'price') return 'Precio';
  if (value === 'cost') return 'Coste';

  return 'Principal componente';
}


function limitationText(
  code: string,
): string {
  if (code === 'comparison_required') {
    return 'Se necesita un periodo comparado para estudiar el cambio.';
  }

  if (
    code
    === 'comparison_required_for_change_analysis'
  ) {
    return 'La atribución del cambio requiere comparación temporal.';
  }

  if (
    code
    === 'unresolved_cost_rows_excluded_from_margin_attribution'
  ) {
    return 'Las filas con coste no resuelto quedan fuera de la atribución de margen.';
  }

  if (
    code
    === 'continued_relationships_not_eligible_for_decomposition'
  ) {
    return 'Algunas relaciones continuas no cumplen las condiciones para descomponer cantidad, precio y coste.';
  }

  if (
    code
    === 'current_period_extends_outside_dataset'
  ) {
    return 'El periodo actual incluye fechas fuera del histórico disponible.';
  }

  if (
    code
    === 'comparison_period_extends_outside_dataset'
  ) {
    return 'El periodo comparado incluye fechas fuera del histórico disponible.';
  }

  if (
    code
    === 'reconciliation_outside_numeric_tolerance'
  ) {
    return 'La reconciliación supera la tolerancia numérica contractual.';
  }

  return code.split('_').join(' ');
}


function dominantFindingCopy(
  finding: MarginCenterProductEconomicStudyFinding,
  currencyCode: string | null,
): {
  title: string;
  body: string;
  toneValue: number | null;
} | null {
  if (
    finding.code
    !== 'eligible_decomposition_balance'
  ) {
    return null;
  }

  const eligibleCount = readNumber(
    finding.values.eligible_relationship_count,
  );

  const dominantEffect = readString(
    finding.values.dominant_effect,
  );

  const dominantAmount = readNumber(
    finding.values.dominant_effect_amount,
  );

  const dominantShare = readNumber(
    finding.values
      .dominant_effect_share_of_absolute_effects_pct,
  );

  return {
    title:
      `${effectLabel(
        dominantEffect,
      )}: ${formatSignedMoney(
        dominantAmount,
        currencyCode,
      )}`,
    body:
      `Es el componente con más peso en ${formatNumber(
        eligibleCount,
        0,
      )} relaciones comparables (${formatPercentage(
        dominantShare,
      )} del total de cantidad, precio y coste).`,
    toneValue: dominantAmount,
  };
}


function investigationPatternCopy(
  pattern:
    MarginCenterProductEconomicStudyInvestigationPattern,
  currencyCode: string | null,
): {
  title: string;
  tag: string;
  body: string;
  toneValue: number | null;
} {
  const count = pattern.relationship_count;
  const metrics = pattern.metrics;

  if (
    pattern.code
    === 'current_only_customers'
  ) {
    return {
      title:
        `${formatNumber(
          count,
          0,
        )} clientes tienen actividad solo en el periodo actual`,
      tag: 'Actividad solo actual',
      body:
        `En conjunto aportan ${formatSignedMoney(
          metrics.margin_delta_documented,
          currencyCode,
        )} de margen y ${formatSignedMoney(
          metrics.revenue_delta,
          currencyCode,
        )} de ingresos.`,
      toneValue:
        metrics.margin_delta_documented,
    };
  }

  if (
    pattern.code
    === 'comparison_only_customers'
  ) {
    return {
      title:
        `${formatNumber(
          count,
          0,
        )} clientes tienen actividad solo en el periodo comparado`,
      tag: 'Actividad solo comparada',
      body:
        `Frente al periodo comparado representan ${formatSignedMoney(
          metrics.margin_delta_documented,
          currencyCode,
        )} de margen y ${formatSignedMoney(
          metrics.revenue_delta,
          currencyCode,
        )} de ingresos.`,
      toneValue:
        metrics.margin_delta_documented,
    };
  }

  if (
    pattern.code
    === 'revenue_up_margin_down'
  ) {
    return {
      title:
        `${formatNumber(
          count,
          0,
        )} ${
          count === 1
            ? 'cliente genera'
            : 'clientes generan'
        } más ingresos pero menos margen`,
      tag: 'Rentabilidad',
      body:
        `Los ingresos cambian ${formatSignedMoney(
          metrics.revenue_delta,
          currencyCode,
        )}, mientras el margen cambia ${formatSignedMoney(
          metrics.margin_delta_documented,
          currencyCode,
        )}.`,
      toneValue:
        metrics.margin_delta_documented,
    };
  }

  if (
    pattern.code
    === 'revenue_down_margin_up'
  ) {
    return {
      title:
        `${formatNumber(
          count,
          0,
        )} ${
          count === 1
            ? 'cliente genera'
            : 'clientes generan'
        } menos ingresos pero más margen`,
      tag: 'Rentabilidad',
      body:
        `Los ingresos cambian ${formatSignedMoney(
          metrics.revenue_delta,
          currencyCode,
        )}, mientras el margen cambia ${formatSignedMoney(
          metrics.margin_delta_documented,
          currencyCode,
        )}.`,
      toneValue:
        metrics.margin_delta_documented,
    };
  }

  if (
    pattern.code
    === 'price_up_quantity_down'
  ) {
    return {
      title:
        `${formatNumber(
          count,
          0,
        )} relaciones con precio al alza y cantidad a la baja`,
      tag: 'Precio / volumen',
      body:
        `El efecto precio suma ${formatSignedMoney(
          metrics.price_effect,
          currencyCode,
        )} y el efecto cantidad ${formatSignedMoney(
          metrics.quantity_effect,
          currencyCode,
        )}. Coinciden ambos movimientos; no implica que uno cause el otro.`,
      toneValue:
        metrics.margin_delta_documented,
    };
  }

  return {
    title:
      `${formatNumber(
        count,
        0,
      )} relaciones con coste unitario al alza y margen a la baja`,
    tag: 'Coste',
    body:
      `En conjunto, los ingresos cambian ${formatSignedMoney(
        metrics.revenue_delta,
        currencyCode,
      )} y el margen ${formatSignedMoney(
        metrics.margin_delta_documented,
        currencyCode,
      )}. Coinciden ambos movimientos; no implica causalidad.`,
    toneValue:
      metrics.margin_delta_documented,
  };
}


function MetricBlock({
  label,
  value,
  detail,
  valueColor = '#f8fafc',
}: {
  label: string;
  value: string;
  detail?: string;
  valueColor?: string;
}) {
  return (
    <Box
      sx={{
        minWidth: 0,
        p: 1.5,
        borderRadius: 2.5,
        border:
          '1px solid rgba(148,163,184,0.14)',
        bgcolor:
          'rgba(15,23,42,0.42)',
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: '#94a3b8',
          fontWeight: 750,
          letterSpacing: '0.02em',
        }}
      >
        {label}
      </Typography>

      <Typography
        sx={{
          mt: 0.45,
          color: valueColor,
          fontWeight: 850,
          fontSize: {
            xs: '1.15rem',
            md: '1.3rem',
          },
          lineHeight: 1.2,
          overflowWrap: 'anywhere',
        }}
      >
        {value}
      </Typography>

      {detail && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mt: 0.55,
            color: '#64748b',
            lineHeight: 1.45,
          }}
        >
          {detail}
        </Typography>
      )}
    </Box>
  );
}


export default function ProductEconomicStudyPanel({
  productId,
  startDate,
  endDate,
  comparisonMode,
  comparisonStartDate,
  comparisonEndDate,
  currencyCode,
}: ProductEconomicStudyPanelProps) {
  const [
    searchParams,
    setSearchParams,
  ] = useSearchParams();

  const [
    detailsOpen,
    setDetailsOpen,
  ] = useState(false);

  const [
    openPatternCode,
    setOpenPatternCode,
  ] = useState<
    MarginCenterProductEconomicStudyInvestigationPatternCode
    | null
  >(null);

  const params =
    useMemo<MarginCenterProductEconomicStudyParams>(
      () => ({
        start_date:
          startDate ?? undefined,
        end_date:
          endDate ?? undefined,
        comparison_mode:
          comparisonMode,
        comparison_start_date:
          comparisonMode === 'custom'
            ? comparisonStartDate ?? undefined
            : undefined,
        comparison_end_date:
          comparisonMode === 'custom'
            ? comparisonEndDate ?? undefined
            : undefined,
        sort_field:
          'absolute_margin_delta',
        sort_direction: 'desc',
        limit: 25,
      }),
      [
        comparisonEndDate,
        comparisonMode,
        comparisonStartDate,
        endDate,
        startDate,
      ],
    );

  const studyQuery = useQuery({
    queryKey: [
      'margin-center',
      'product-economic-study',
      productId,
      params,
    ],
    queryFn: () =>
      getMarginCenterProductEconomicStudy(
        productId,
        params,
      ),
    enabled: Boolean(productId),
    placeholderData:
      (previousData) => previousData,
    retry: false,
  });

  const data = studyQuery.data;

  const openRelationship = (
    customerId: string,
  ) => {
    const next =
      new URLSearchParams(searchParams);

    next.delete(
      'relationshipProduct',
    );

    next.set(
      'relationshipCustomer',
      customerId,
    );

    setSearchParams(next);
  };

  if (
    studyQuery.isLoading
    && !data
  ) {
    return (
      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          border:
            '1px solid rgba(148,163,184,0.18)',
          bgcolor:
            'rgba(15,23,42,0.54)',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            p: {
              xs: 2,
              md: 2.5,
            },
          }}
        >
          <Stack spacing={1.25}>
            <Typography
              variant="h6"
              sx={{
                color: '#f8fafc',
                fontWeight: 850,
              }}
            >
              Estudio económico
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: '#94a3b8',
              }}
            >
              Analizando qué explica el cambio económico de este producto…
            </Typography>

            <LinearProgress />
          </Stack>
        </Box>
      </Paper>
    );
  }

  if (studyQuery.isError) {
    return (
      <Alert severity="error">
        No se ha podido calcular el estudio económico del producto.
      </Alert>
    );
  }

  if (!data) {
    return null;
  }

  if (
    data.status
    === 'empty_dataset'
  ) {
    return (
      <Alert severity="info">
        No existe actividad transaccional para construir el estudio económico.
      </Alert>
    );
  }

  if (
    data.status
      === 'product_not_found'
    || !data.product
    || !data.financial
  ) {
    return (
      <Alert severity="warning">
        No se ha encontrado evidencia suficiente para construir el estudio económico de este producto.
      </Alert>
    );
  }

  const effectiveCurrencyCode =
    data.presentation.currency_code
    ?? currencyCode;

  const comparisonAvailable =
    data.selection.comparison_mode
      !== 'none'
    && data.financial.comparison
      !== null
    && data.financial.changes
      !== null;

  const coverage =
    data.analysis_coverage;

  const reconciliation =
    data.reconciliation;

  const contributions =
    data.contributions;

  const dominantFinding =
    data.findings
      .map((finding) =>
        dominantFindingCopy(
          finding,
          effectiveCurrencyCode,
        )
      )
      .find(
        (
          finding,
        ): finding is NonNullable<
          ReturnType<
            typeof dominantFindingCopy
          >
        > => finding !== null,
      )
      ?? null;

  const topRelationships =
    contributions?.top_absolute
    ?? [];

  const decomposition =
    contributions?.decomposition
    ?? null;

  const investigationPatterns =
    comparisonAvailable
      ? data.investigation_patterns
          .slice(0, 4)
      : [];

  const statusLabel =
    reconciliation?.reconciled
      === true
      ? 'Cálculo comprobado'
      : data.status === 'partial'
        ? 'Datos parciales'
        : data.status
            === 'insufficient_evidence'
          ? 'Faltan datos'
          : 'Análisis disponible';

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 4,
        border:
          '1px solid rgba(56,189,248,0.22)',
        bgcolor:
          'rgba(15,23,42,0.62)',
        overflow: 'hidden',
      }}
    >
      {studyQuery.isFetching && (
        <LinearProgress />
      )}

      <Box
        sx={{
          p: {
            xs: 2,
            md: 2.75,
          },
        }}
      >
        <Stack spacing={2.25}>
          <Stack
            direction={{
              xs: 'column',
              md: 'row',
            }}
            spacing={1.5}
            alignItems={{
              xs: 'flex-start',
              md: 'center',
            }}
            justifyContent="space-between"
          >
            <Box>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
              >
                <InsightsRoundedIcon
                  sx={{
                    color: '#38bdf8',
                    fontSize: 22,
                  }}
                />

                <Typography
                  variant="h6"
                  sx={{
                    color: '#f8fafc',
                    fontWeight: 900,
                  }}
                >
                  Estudio económico
                </Typography>
              </Stack>

              <Typography
                variant="body2"
                sx={{
                  mt: 0.55,
                  color: '#94a3b8',
                  maxWidth: 860,
                  lineHeight: 1.55,
                }}
              >
                Qué cambios económicos merece revisar y en qué clientes conviene profundizar.
              </Typography>
            </Box>

            <Chip
              size="small"
              label={statusLabel}
              sx={{
                bgcolor:
                  'rgba(56,189,248,0.10)',
                color: '#bae6fd',
                border:
                  '1px solid rgba(56,189,248,0.18)',
                fontWeight: 800,
              }}
            />
          </Stack>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                xl:
                  'minmax(300px, 0.34fr) minmax(0, 0.66fr)',
              },
              gap: {
                xs: 1.5,
                xl: 2,
              },
              alignItems: 'start',
            }}
          >
            <Stack spacing={1}>
              <Typography
                variant="subtitle2"
                sx={{
                  color: '#e2e8f0',
                  fontWeight: 850,
                  mb: 0.15,
                }}
              >
                Profundidad del análisis
              </Typography>

              {comparisonAvailable
                && dominantFinding && (
                <MetricBlock
                  label="Principal componente del cambio"
                  value={
                    dominantFinding.title
                  }
                  valueColor={
                    movementColor(
                      dominantFinding
                        .toneValue,
                    )
                  }
                  detail={
                    dominantFinding.body
                  }
                />
              )}

              <MetricBlock
                label="Cobertura de costes"
                value={formatPercentage(
                  data.financial.current
                    .cost_coverage_revenue_pct,
                )}
                detail={
                  `${formatPercentage(
                    data.financial.current
                      .cost_coverage_rows_pct,
                  )} de operaciones con coste conocido`
                }
              />

              <MetricBlock
                label="Clientes con desglose"
                value={
                  coverage
                    ? `${formatNumber(
                        coverage
                          .decomposition_eligible_relationships,
                        0,
                      )} / ${formatNumber(
                        coverage
                          .relationships_total,
                        0,
                      )}`
                    : '—'
                }
                detail={
                  coverage
                    ? `${formatPercentage(
                        coverage
                          .eligible_share_of_absolute_documented_movement_pct,
                      )} de los cambios de margen cubiertos`
                    : undefined
                }
              />
            </Stack>

            <Stack spacing={1}>
              <Typography
                variant="subtitle2"
                sx={{
                  color: '#e2e8f0',
                  fontWeight: 850,
                  mb: 0.15,
                }}
              >
                Situaciones detectadas
              </Typography>

              {!comparisonAvailable && (
                <Alert severity="info">
                  Selecciona una comparación temporal para detectar situaciones comerciales que merecen revisión.
                </Alert>
              )}

              {comparisonAvailable
                && investigationPatterns.length
                  === 0 && (
                <Box
                  sx={{
                    px: 1.5,
                    py: 1.4,
                    borderRadius: 2.5,
                    border:
                      '1px solid rgba(148,163,184,0.14)',
                    bgcolor:
                      'rgba(15,23,42,0.30)',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#94a3b8',
                    }}
                  >
                    No se han detectado patrones prioritarios con la evidencia disponible.
                  </Typography>
                </Box>
              )}

              {investigationPatterns
                .map((pattern) => {
                  const copy =
                    investigationPatternCopy(
                      pattern,
                      effectiveCurrencyCode,
                    );

                  const isOpen =
                    openPatternCode
                    === pattern.code;

                  return (
                    <Box
                      key={pattern.code}
                      sx={{
                        px: 1.5,
                        py: 1.15,
                        borderRadius: 2.5,
                        border:
                          '1px solid rgba(148,163,184,0.14)',
                        bgcolor:
                          'rgba(15,23,42,0.30)',
                      }}
                    >
                      <Stack
                        direction={{
                          xs: 'column',
                          md: 'row',
                        }}
                        spacing={1}
                        alignItems={{
                          xs: 'stretch',
                          md: 'center',
                        }}
                        justifyContent="space-between"
                      >
                        <Box
                          sx={{
                            minWidth: 0,
                          }}
                        >
                          <Stack
                            direction="row"
                            spacing={0.75}
                            alignItems="center"
                            flexWrap="wrap"
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color:
                                  movementColor(
                                    copy.toneValue,
                                  ),
                                fontWeight: 850,
                              }}
                            >
                              {copy.title}
                            </Typography>

                            <Chip
                              size="small"
                              label={copy.tag}
                              sx={{
                                height: 20,
                                bgcolor:
                                  'rgba(56,189,248,0.08)',
                                color:
                                  '#bae6fd',
                                border:
                                  '1px solid rgba(56,189,248,0.18)',
                                fontSize:
                                  '0.67rem',
                                fontWeight: 750,
                              }}
                            />
                          </Stack>

                          <Typography
                            variant="body2"
                            sx={{
                              mt: 0.3,
                              color:
                                '#94a3b8',
                              lineHeight:
                                1.45,
                            }}
                          >
                            {copy.body}
                          </Typography>

                          {pattern.metrics
                            .share_of_absolute_documented_movement_pct
                            !== null && (
                            <Typography
                              variant="caption"
                              sx={{
                                display:
                                  'block',
                                mt: 0.35,
                                color:
                                  '#64748b',
                              }}
                            >
                              {formatPercentage(
                                pattern.metrics
                                  .share_of_absolute_documented_movement_pct,
                              )} del movimiento absoluto de margen.
                            </Typography>
                          )}
                        </Box>

                        {pattern
                          .top_relationships
                          .length > 0 && (
                          <Button
                            size="small"
                            variant="text"
                            endIcon={
                              isOpen
                                ? <ExpandLessRoundedIcon />
                                : <ExpandMoreRoundedIcon />
                            }
                            onClick={() =>
                              setOpenPatternCode(
                                isOpen
                                  ? null
                                  : pattern.code,
                              )
                            }
                            sx={{
                              flexShrink: 0,
                            }}
                          >
                            Ver evidencia (
                            {
                              pattern
                                .top_relationships
                                .length
                            }
                            )
                          </Button>
                        )}
                      </Stack>

                      <Collapse in={isOpen}>
                        <Stack
                          spacing={0.5}
                          sx={{
                            mt: 0.75,
                            pt: 0.75,
                            borderTop:
                              '1px solid rgba(148,163,184,0.10)',
                          }}
                        >
                          {pattern
                            .top_relationships
                            .map(
                              (
                                relationship,
                              ) => (
                                <Box
                                  key={
                                    relationship
                                      .customer_id
                                  }
                                  sx={{
                                    display:
                                      'grid',
                                    gridTemplateColumns:
                                      {
                                        xs:
                                          '1fr',
                                        sm:
                                          'minmax(0, 1fr) auto auto',
                                      },
                                    gap: 1,
                                    alignItems:
                                      'center',
                                    px: 1,
                                    py: 0.7,
                                    borderRadius:
                                      2,
                                    bgcolor:
                                      'rgba(2,6,23,0.20)',
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color:
                                        '#e2e8f0',
                                      fontWeight:
                                        750,
                                    }}
                                  >
                                    {
                                      relationship
                                        .label
                                    }
                                  </Typography>

                                  <Stack
                                    spacing={0}
                                    sx={{
                                      textAlign:
                                        'right',
                                    }}
                                  >
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color:
                                          movementColor(
                                            relationship
                                              .margin_delta_documented,
                                          ),
                                        fontWeight:
                                          800,
                                      }}
                                    >
                                      Margen{' '}
                                      {formatSignedMoney(
                                        relationship
                                          .margin_delta_documented,
                                        effectiveCurrencyCode,
                                      )}
                                    </Typography>

                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color:
                                          '#64748b',
                                      }}
                                    >
                                      Ingresos{' '}
                                      {formatSignedMoney(
                                        relationship
                                          .revenue_delta,
                                        effectiveCurrencyCode,
                                      )}
                                    </Typography>
                                  </Stack>

                                  <Button
                                    size="small"
                                    variant="text"
                                    endIcon={
                                      <OpenInNewRoundedIcon />
                                    }
                                    onClick={() =>
                                      openRelationship(
                                        relationship
                                          .customer_id,
                                      )
                                    }
                                  >
                                    Examinar
                                  </Button>
                                </Box>
                              ),
                            )}
                        </Stack>
                      </Collapse>
                    </Box>
                  );
                })}
            </Stack>
          </Box>

          {data.limitations.length > 0 && (
            <Alert severity="info">
              {data.limitations
                .map(limitationText)
                .join(' ')}
            </Alert>
          )}

          {comparisonAvailable
            && contributions && (
            <>
              <Divider />

              <Box>
                <Button
                  variant="outlined"
                  startIcon={
                    <AccountTreeRoundedIcon />
                  }
                  endIcon={
                    detailsOpen
                      ? <ExpandLessRoundedIcon />
                      : <ExpandMoreRoundedIcon />
                  }
                  onClick={() =>
                    setDetailsOpen(
                      (current) =>
                        !current,
                    )
                  }
                >
                  Ver qué clientes explican el cambio
                </Button>

                <Collapse in={detailsOpen}>
                  <Box
                    sx={{
                      mt: 2,
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        xl:
                          'minmax(300px, 0.8fr) repeat(2, minmax(0, 1fr))',
                      },
                      gap: 1.5,
                      alignItems:
                        'start',
                    }}
                  >
                    {decomposition
                      && decomposition
                        .eligible_relationship_count
                        > 0 && (
                      <Box
                        sx={{
                          minWidth: 0,
                          gridColumn: {
                            xs: '1',
                            xl: '1',
                          },
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{
                            color:
                              '#e2e8f0',
                            fontWeight:
                              850,
                            mb: 1,
                          }}
                        >
                          Qué ha movido el margen en las relaciones comparables
                        </Typography>

                        <Box
                          sx={{
                            display:
                              'grid',
                            gridTemplateColumns:
                              {
                                xs:
                                  '1fr',
                                sm:
                                  'repeat(3, minmax(0, 1fr))',
                                xl:
                                  '1fr',
                              },
                            gap: 1,
                          }}
                        >
                          <MetricBlock
                            label="Por cantidad"
                            value={formatSignedMoney(
                              decomposition
                                .quantity_effect,
                              effectiveCurrencyCode,
                            )}
                            valueColor={movementColor(
                              decomposition
                                .quantity_effect,
                            )}
                          />

                          <MetricBlock
                            label="Por precio"
                            value={formatSignedMoney(
                              decomposition
                                .price_effect,
                              effectiveCurrencyCode,
                            )}
                            valueColor={movementColor(
                              decomposition
                                .price_effect,
                            )}
                          />

                          <MetricBlock
                            label="Por coste"
                            value={formatSignedMoney(
                              decomposition
                                .cost_effect,
                              effectiveCurrencyCode,
                            )}
                            valueColor={movementColor(
                              decomposition
                                .cost_effect,
                            )}
                          />
                        </Box>

                        <Typography
                          variant="caption"
                          sx={{
                            display:
                              'block',
                            mt: 1,
                            color:
                              '#64748b',
                            lineHeight:
                              1.5,
                          }}
                        >
                          Este reparto muestra cuánto cambia el margen por cantidad, precio y coste dentro de las relaciones cliente-producto comparables. No explica causalmente por qué un cliente compró más o menos.
                        </Typography>
                      </Box>
                    )}

                    <Box
                      sx={{
                        minWidth: 0,
                        gridColumn: {
                          xs: '1',
                          xl:
                            '2 / span 2',
                        },
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          color:
                            '#e2e8f0',
                          fontWeight:
                            850,
                          mb: 1,
                        }}
                      >
                        Clientes con mayor cambio de margen
                      </Typography>

                      {topRelationships
                        .length === 0 ? (
                        <Typography
                          variant="body2"
                          sx={{
                            color:
                              '#94a3b8',
                          }}
                        >
                          No existen relaciones con variación documentada para esta comparación.
                        </Typography>
                      ) : (
                        <Box
                          sx={{
                            display:
                              'grid',
                            gridTemplateColumns:
                              {
                                xs:
                                  '1fr',
                                xl:
                                  'repeat(2, minmax(0, 1fr))',
                              },
                            gap: 0.75,
                          }}
                        >
                          {topRelationships
                            .map(
                              (
                                relationship,
                              ) => (
                                <Box
                                  key={
                                    relationship
                                      .customer_id
                                  }
                                  sx={{
                                    display:
                                      'grid',
                                    gridTemplateColumns:
                                      {
                                        xs:
                                          '1fr',
                                        md:
                                          'minmax(0, 1fr) auto auto',
                                      },
                                    gap: 1,
                                    alignItems:
                                      'center',
                                    px: 1.25,
                                    py: 1,
                                    borderRadius:
                                      2,
                                    border:
                                      '1px solid rgba(148,163,184,0.12)',
                                    bgcolor:
                                      'rgba(15,23,42,0.30)',
                                  }}
                                >
                                  <Box
                                    sx={{
                                      minWidth:
                                        0,
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color:
                                          '#f8fafc',
                                        fontWeight:
                                          750,
                                      }}
                                    >
                                      {
                                        relationship
                                          .label
                                      }
                                    </Typography>

                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color:
                                          '#64748b',
                                      }}
                                    >
                                      {relationship
                                        .observation_class
                                        === 'continued'
                                        ? 'Actividad en ambos periodos'
                                        : relationship
                                            .observation_class
                                            === 'current_only'
                                          ? 'Actividad solo actual'
                                          : relationship
                                              .observation_class
                                              === 'comparison_only'
                                            ? 'Actividad solo comparada'
                                            : 'Sin comparación'}
                                      {relationship
                                        .decomposition_eligible
                                        ? ' · desglose disponible'
                                        : ''}
                                    </Typography>
                                  </Box>

                                  <Stack
                                    direction="row"
                                    spacing={1.5}
                                    sx={{
                                      minWidth:
                                        {
                                          md:
                                            220,
                                        },
                                    }}
                                  >
                                    <Box>
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color:
                                            '#64748b',
                                        }}
                                      >
                                        Δ margen
                                      </Typography>

                                      <Typography
                                        variant="body2"
                                        sx={{
                                          color:
                                            movementColor(
                                              relationship
                                                .margin_delta_documented,
                                            ),
                                          fontWeight:
                                            850,
                                        }}
                                      >
                                        {formatSignedMoney(
                                          relationship
                                            .margin_delta_documented,
                                          effectiveCurrencyCode,
                                        )}
                                      </Typography>
                                    </Box>

                                    <Box>
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color:
                                            '#64748b',
                                        }}
                                      >
                                        Peso
                                      </Typography>

                                      <Typography
                                        variant="body2"
                                        sx={{
                                          color:
                                            '#e2e8f0',
                                          fontWeight:
                                            800,
                                        }}
                                      >
                                        {formatPercentage(
                                          relationship
                                            .share_of_absolute_documented_movement_pct,
                                        )}
                                      </Typography>
                                    </Box>
                                  </Stack>

                                  <Button
                                    size="small"
                                    variant="text"
                                    endIcon={
                                      <OpenInNewRoundedIcon />
                                    }
                                    onClick={() =>
                                      openRelationship(
                                        relationship
                                          .customer_id,
                                      )
                                    }
                                  >
                                    Examinar
                                  </Button>
                                </Box>
                              ),
                            )}
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Collapse>
              </Box>
            </>
          )}
        </Stack>
      </Box>
    </Paper>
  );
}
